import { Box, Button, InputAdornment, SvgIcon, TextField, Typography } from '@mui/material';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getTranslatedValue } from 'src/common/utils/format-utils';
import { References } from 'src/components/references';
import { useAuth, useDialog, useResources } from '../../../hooks';
import { deleteDirection, getDirections } from '../../../thunks/direction-thunks';
import { DataTable } from '../../data-table';
import { DataTableExtendedFilter } from '../../data-table/data-table-extended-filter';
import { DataTableFooter } from '../../data-table/data-table-footer';
import { ConfirmDialog } from '../../dialogs';
import { Icons, PlusIcon } from '../../icons';
import { OrganizationsSelect, OrganizationsTableActions } from '../../organizations';
import { DirectionsEditDialog } from '../directions-edit-dialog';

export const DirectionsList: ComponentType = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [openDirectionsEdit, closeDirectionsEdit] = useDialog(DirectionsEditDialog);
  const [openConfirm, closeConfirm] = useDialog(ConfirmDialog);
  const { isSuperAdmin } = useAuth();
  const {
    list: { hits, isLoading, total },
    query,
    handleQueryUpdate,
    handleFetchFirstPage,
    handleFetchCurrentPage,
  } = useResources({
    entity: 'direction',
    onGetAll: getDirections,
    defaultParams: { embed: 'organization' },
  });

  const handleCreate = () => {
    openDirectionsEdit({
      title: t('directions.add', 'Добавить направление'),
      onSubmit: () => {
        closeDirectionsEdit();
        handleFetchFirstPage();
      },
    });
  };

  const handleEdit = (id: string | number) => {
    openDirectionsEdit({
      title: t('directions.edit', 'Редактировать направление'),
      id,
      onSubmit: () => {
        closeDirectionsEdit();
        handleFetchCurrentPage();
      },
    });
  };

  const handleDelete = (id: string | number) => {
    openConfirm({
      size: 'small',
      resolve: () => {
        closeConfirm();
        deleteDirection(id)(dispatch).then(() => {
          handleFetchFirstPage();
        });
      },
    });
  };

  const data = {
    hits,
    isLoading,
    columns: [
      {
        width: 120,
        label: '№',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <Typography variant="h5" color="primary">
            {item.id}
          </Typography>
        ),
      },
      {
        width: 256,
        label: 'Направление',
        labelKey: 'direction',
        renderItem: (item: Record<string, any>) => getTranslatedValue(item.title, i18n.language),
      },
      {
        width: 384,
        label: 'Организация',
        labelKey: 'organization',
        renderItem: (item: Record<string, any>) =>
          getTranslatedValue(item.organization.title, i18n.language),
      },
      {
        width: 'auto',
        label: 'Описание',
        labelKey: 'description',
        renderItem: (item: Record<string, any>) =>
          getTranslatedValue(item.description, i18n.language),
      },
      {
        width: 96,
        label: '',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <OrganizationsTableActions item={item} onEdit={handleEdit} onDelete={handleDelete} />
        ),
      },
    ],
  };

  return (
    <References>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <DataTableExtendedFilter
          {...{
            initialValues: query,
            onSubmit: handleQueryUpdate,
          }}
        >
          {(formik) => {
            return (
              <>
                <TextField
                  {...formik.getFieldProps('search')}
                  label={t('search', 'Поиск')}
                  placeholder={t('search', 'Поиск')}
                  size="small"
                  sx={{ width: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon component={Icons.Search} viewBox="0 0 24 24" color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                {isSuperAdmin && (
                  <Box>
                    <OrganizationsSelect
                      {...formik.getFieldProps('organization')}
                      label={t('profile.organization', 'Организация')}
                      placeholder={t('profile.organization.placeholder', 'Выберите организацию')}
                      size="small"
                      prompt={t('all', 'Все')}
                      sx={{ width: 300 }}
                    />
                  </Box>
                )}
              </>
            );
          }}
        </DataTableExtendedFilter>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="secondary"
          startIcon={<PlusIcon />}
          size="small"
          sx={{ whiteSpace: 'nowrap' }}
        >
          {t('directions.add', 'Добавить направление')}
        </Button>
      </Box>
      <DataTable {...data} />
      <DataTableFooter {...{ total, query, onUpdate: handleQueryUpdate }} />
    </References>
  );
};
